import { Component, effect, inject, OnInit } from '@angular/core';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { Image } from '../../../models/image';
import { ImageStore } from '../../../stores/image/image.state';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService, getUploadPath } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { getState } from '@ngrx/signals';
import { NgIf, NgOptimizedImage } from '@angular/common';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { FileUploadEvent, FileUploadModule } from 'primeng/fileupload';
import { ImageService } from '../../../services/image/image.service';
import { MessageService } from 'primeng/api';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { MessageModule } from 'primeng/message';
import { AlertComponent } from '../../atoms/alert/alert.component';

@Component({
  selector: 'app-image-form',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    FormFieldWrapperComponent,
    InputTextModule,
    DividerModule,
    FormFooterComponent,
    FileUploadModule,
    NgOptimizedImage,
    MessageModule,
    AlertComponent,
  ],
  templateUrl: './image-form.component.html',
  styleUrl: './image-form.component.scss',
})
export class ImageFormComponent
  extends BaseFormComponent<Image>
  implements OnInit
{
  readonly store = inject(ImageStore);
  readonly activeUserStore = inject(ActiveUserStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: Image;
  override formInitialized = false;
  uploadUrl = '';
  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder,
    private imageService: ImageService,
    private messageService: MessageService
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    effect(() => {
      const state = getState(this.store);
      if (state.selectedId) {
        this.initializeForm();
      }
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.uploadUrl = this.imageService.getUploadUrl();
  }

  /**
   * Initializes the form with default values and validators based on the selected item.
   * Retrieves the selected item from the store and uses it to populate the form fields.
   *
   * @return {void} Does not return a value.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();

    this.form = this.fb.group({
      title: [this.itemToEdit?.title || '', Validators.required],
      source: [this.itemToEdit?.source || '', Validators.required],
      src: [this.itemToEdit?.src, Validators.required],
    });
    this.formInitialized = true;
  }

  onUpload($event: FileUploadEvent | any): void {
    if ($event.originalEvent.status !== 200) {
      this.messageService.add({
        severity: 'danger',
        summary: 'Upload fehlgeschlagen',
        detail: $event.originalEvent.body.message,
      });
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'Upload erfolgreich!',
      });

      this.form.patchValue({ src: $event.originalEvent.body.filePath });
    }
  }

  get imagePath() {
    return getUploadPath(
      `imagePool/${this.activeUserStore.commune()}`,
      this.form.value.src
    );
  }

  onCancel() {
    if (this.itemToEdit?.src && this.itemToEdit?.src !== this.form.value.src) {
      this.removeImage(this.form.value.src);
    }
  }

  removeImage(src: string) {
    this.imageService.removeImageSrc(src, this.activeUserStore.commune());
  }

  /**
   * Handles the submission process by invoking the submit method.
   *
   * @return {void} Does not return a value.
   */
  onSubmit(): void {
    if (this.itemToEdit?.src && this.itemToEdit?.src !== this.form.value.src) {
      this.removeImage(this.itemToEdit?.src);
    }
    this.submit();
  }
}
